import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import {ActivatedRoute, Data, Router} from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import {of, Subject} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {User, UserNode} from 'app/core/user/user.model';
import { UserService } from 'app/core/user/user.service';
import {AuthService} from '../../../core/auth/auth.service';
import {Condominium, CondominiumPaginate} from '../../../modules/condominium/condominium.types';
import {CondominiumService} from '../../../modules/condominium/condominium.service';
import {CipaFacilConfirmationService} from '../../../../@cipafacil/services/confirmation';
import {environment} from '../../../../environments/environment';
import {AppLayoutComponent} from '../../layouts/app/app.component';
import {AppService} from "../../../app.service";

@Component({
    selector: 'user-menu',
    templateUrl: './user-menu.component.html',
    styleUrls: ['./user-menu.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'userMenu',
    providers: [AppLayoutComponent]
})
export class UserMenuComponent implements OnInit, OnDestroy
{
    static ngAcceptInputType_showAvatar: BooleanInput;

    @Input() showAvatar: boolean = true;
    @Input() dark: boolean = false;
    @Input() isCondominiumPage: boolean = false;

    user: UserNode;
    condominiums: Condominium[];
    layoutAdmin: boolean = false;
    hasPermissionAdminView: boolean = false;
    env = environment;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _condominiumService: CondominiumService,
        private _cipaFacilConfirmationDialog: CipaFacilConfirmationService,
        public authService: AuthService,
        public appLayoutComponent: AppLayoutComponent,
        public appService: AppService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to the resolved route mock-api
        this._activatedRoute.data.subscribe(data => {
            this.layoutAdmin = data.layout && data.layout === 'admin';
        });

        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: UserNode) => {
                this.user = user;

                this.hasPermissionAdminView = user.user_permissions.some(x => x.permission.code === 'admin_view') || !!user.is_admin;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        this._condominiumService.getAll()
            .subscribe((paginate: CondominiumPaginate) => {
                this.condominiums = paginate.items;
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign out
     */
    signOut(): void
    {
        this.authService.signOut()
            .subscribe(() => {
                window.location.reload();
            });
    }

    /**
     * Change company
     */
    changeCondominium(condominium: Condominium): void
    {
        this._cipaFacilConfirmationDialog.open({
            title: 'Deseja mudar para o condomínio: ' + condominium.name + '?',
            message: 'Tem certeza de que deseja confirmar esta ação?',
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation',
                color: 'primary'
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Confirmar',
                    color: 'primary'
                },
                cancel: {
                    show: true,
                    label: 'Cancelar'
                }
            },
            dismissible: false
        })
            .afterClosed()
            .subscribe((action) => {
                if (action === 'confirmed') {
                    this._condominiumService.select(condominium.id)
                        .subscribe(() => {
                            window.location.reload();
                        });
                }
            });
    }
}
